import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList, Tooltip } from 'recharts';
import { useGetChannelPerformanceQuery, useGetOrdersByChannelQuery } from 'Services/Admin/DashboardApiSlice';

const COLORS = ['#1E88E5', '#2B5B84', '#4CAF50', '#FF7043', '#FFC107', '#9C27B0'];

export const ChannelPerformance = ({ 
  fromDate, 
  toDate, 
  warehouse, 
  categoryId, 
  productId,
}: { 
  fromDate: string; 
  toDate: string; 
  warehouse?: string; 
  categoryId?: string; 
  productId?: string;
  mockMultiplier?: number; 
}) => {
  const plant = process.env.REACT_APP_PLANT || '';

  const { data: pieRaw, isLoading: isPieLoading, isFetching: isPieFetching } = useGetChannelPerformanceQuery(
    { plant, fromDate, toDate, warehouse, categoryId, productId },
    { pollingInterval: 60000, refetchOnMountOrArgChange: true }
  );
  const { data: barRaw, isLoading: isBarLoading, isFetching: isBarFetching } = useGetOrdersByChannelQuery(
    { plant, fromDate, toDate, warehouse, categoryId, productId },
    { pollingInterval: 60000, refetchOnMountOrArgChange: true }
  );

  const isAnyLoading = isPieLoading || isBarLoading || isPieFetching || isBarFetching;

  const pieData = React.useMemo(() => {
    const raw = pieRaw?.results || pieRaw?.data || pieRaw || [];
    const realList = Array.isArray(raw) ? raw : [];
    
    if (realList.length > 0) {
      return realList.map((item: any, i: number) => ({
        name: item.channel || 'Other',
        displayCount: item.orderCount || 0,
        value: item.orderCount || 0,
        color: COLORS[i % COLORS.length],
        isReal: true 
      }));
    }
    return [{ name: '', value: 100, color: '#F8FAFC', isReal: false, displayCount: 0 }];
  }, [pieRaw]);

  const currentBarData = React.useMemo(() => {
    const raw = barRaw?.results || barRaw?.data || barRaw || [];
    const list = Array.isArray(raw) ? raw : [];

    if (list.length > 0) {
      return list.map((item: any) => ({
        name: item.channel || '',
        current: item.currentOrders || 0,
        previous: item.previousOrders || 0
      }));
    }
    return [{ name: '', current: 0, previous: 0 }];
  }, [barRaw]);

  const realPieItems = React.useMemo(() => pieData.filter(item => item.isReal), [pieData]);
  const hasData = realPieItems.length > 0;
  
  const renderCustomizedLabel = (props: any) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, value, isReal, percent } = props;
    if (!isReal || value === 0) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const fontSize = percent < 0.08 ? "10" : "12";

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor="middle" 
        dominantBaseline="central" 
        fontSize={fontSize} 
        fontWeight="900" 
        className="pointer-events-none"
      >
        {value}
      </text>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col min-h-[420px]">
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-6 shrink-0">
        <h3 className="text-[16px] font-bold text-[#1B2559]">Channel Performance</h3>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-4 flex-1 items-stretch relative">
        {isAnyLoading && (
          <div className="absolute inset-0 z-30 bg-white/60 flex items-center justify-center backdrop-blur-[1px]">
            <div className="w-8 h-8 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
        )}
        {!hasData && !isAnyLoading && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center pointer-events-none p-10 text-center">
            <div className="mb-6 w-16 h-16 rounded-full bg-blue-50/50 flex items-center justify-center text-blue-200 shadow-inner">
               <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
                <path d="M22 12A10 10 0 0 0 12 2v10z" />
              </svg>
            </div>
            <h4 className="text-[16px] font-black text-[#1B2559] mb-1">No Activity Found</h4>
            <p className="text-[10px] font-bold text-gray-400">Adjust parameters or check filters.</p>
          </div>
        )}

        <div className={`flex flex-col border-b lg:border-b-0 lg:border-r border-gray-100 pb-8 lg:pb-0 lg:pr-4 transition-all duration-700 ${!hasData ? 'opacity-5' : 'opacity-100'}`}>
          <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center lg:justify-start min-h-[40px] mb-2 px-2">
            {realPieItems.map((item, idx) => (
              <div key={idx} className="flex flex-col leading-tight">
                <span className="text-[14px] font-black text-blue-600">{item.displayCount} Orders</span>
                <span className="text-[8px] text-gray-400 font-bold capitalize tracking-normal">From {item.name.replace("P O S", "POS")}</span>
              </div>
            ))}
          </div>

          <div className="flex-1 min-h-[220px] xl:min-h-[240px] flex items-center justify-center relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 25, right: 25, bottom: 25, left: 25 }}>
                <Pie
                  data={pieData} 
                  innerRadius={40} 
                  outerRadius={65} 
                  paddingAngle={0} 
                  dataKey="value" 
                  stroke="none" 
                  cx="50%" 
                  cy="50%"
                  label={renderCustomizedLabel} 
                  labelLine={false}
                  minAngle={25}
                  cornerRadius={0} 
                >
                  {pieData.map((entry: any, index: number) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                </Pie>
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '12px', fontWeight: '800' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 justify-center">
            {realPieItems.map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5 group cursor-default">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-[10px] font-bold text-gray-400 group-hover:text-[#1B2559] transition-colors capitalize">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`flex flex-col pl-0 lg:pl-4 transition-all duration-700 ${!hasData ? 'opacity-5' : 'opacity-100'}`}>
          <h4 className="text-[15px] font-bold text-[#1B2559] mb-4 text-center lg:text-left">Orders by Channel</h4>
          <div className="flex flex-wrap gap-5 min-h-[40px] mb-2 justify-center lg:justify-start items-center px-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#1E88E5]" />
               <span className="text-[10px] font-bold text-gray-400 tracking-tight">Current</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#CBD5E1]" />
               <span className="text-[10px] font-bold text-gray-400 tracking-tight">Previous</span>
            </div>
          </div>

          <div className="flex-1 w-full min-h-[220px] flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={currentBarData} barSize={12} barGap={8} margin={{ top: 30, bottom: 5, right: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#FAFAFA" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10, fontWeight: 700 }} dy={5} />
                {/* YAxis added but hidden to provide extra domain buffer for top labels */}
                <YAxis hide domain={[0, (dataMax: number) => dataMax * 1.2]} />
                <Bar dataKey="current" fill="#1E88E5" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="current" position="top" offset={10} style={{ fill: '#1B2559', fontSize: 9, fontWeight: '900' }} />
                </Bar>
                <Bar dataKey="previous" fill="#CBD5E1" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="previous" position="top" offset={10} style={{ fill: '#CBD5E1', fontSize: 9, fontWeight: '900' }} />
                </Bar>
                <Tooltip cursor={{ fill: '#F8FAFC' }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
